import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/use-auth';

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [domain, setDomain] = useState(searchParams.get('org') ?? '');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const redirectTo = (location.state as { from?: { pathname?: string } })?.from?.pathname;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!domain.trim()) return;

    setError(null);
    setIsLoading(true);
    try {
      await login(domain.trim(), redirectTo);
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      console.error('SSO login failed:', err);
      setError(`Unable to sign in: ${detail}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img src="/logo.png" alt="Metaonia" className="mx-auto mb-4 h-64 w-auto" />
          <CardTitle className="text-xl">Manager Portal</CardTitle>
          <CardDescription>
            Sign in with your work credentials to access training materials.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="domain">Organization domain</Label>
              <Input
                id="domain"
                type="text"
                placeholder="acme.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                disabled={isLoading}
                autoFocus
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={isLoading || !domain.trim()}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign In
            </Button>
          </form>
          <p className="mt-6 text-center text-xs text-muted-foreground">
            Your employer has enrolled your organization in Metaonia manager training.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
